import { Route, Switch, useLocation, Router } from 'wouter';
import { useAuth } from '@/lib/platform/AuthProvider';
import { AnimatePresence } from 'framer-motion';
import { useEffect } from 'react';
import { Loader } from '@/components/Loader';

// Pages
import AdminDashboard from './pages/Dashboard';
import AdminEvents from './pages/Events';
import AdminMatching from './pages/Matching';
import AdminPayments from './pages/Payments';
import AdminFeedback from './pages/Feedback';
import AdminNeighborhoods from './pages/Neighborhoods';
import AdminUsers from './pages/Users';
import AdminChats from './pages/Chats';
import AdminPolls from './pages/Polls';
import AdminClubs from './pages/Clubs';
import AdminClubDetail from './pages/ClubDetail';
import AdminPollDetail from './pages/PollDetail';
import AdminAnalytics from './pages/Analytics';
import AdminBookings from './pages/Bookings';
import AdminDemographics from './pages/Demographics';
import AdminConnections from './pages/Connections';
import AdminLandingVariants from './pages/LandingVariants';
import AdminLandingVariantEditor from './pages/LandingVariantEditor';
import AdminHostEvents from './pages/HostEvents';
import AdminSocialCrawling from './pages/SocialCrawling';
import AdminKikiReview from './pages/KikiReview';
import AdminOps from './pages/Ops';
import AdminOpsEventDetail from './pages/OpsEventDetail';
import AdminOpsDecisions from './pages/OpsDecisions';
import AdminOpsConductor from './pages/OpsConductor';
import AdminOpsActivity from './pages/OpsActivity';
import AdminOpsNoShows from './pages/OpsNoShows';
import AdminTriage from './pages/Triage';
import AdminTriagePlaybook from './pages/TriagePlaybook';

// Components
import AdminRoute from './components/AdminRoute';
import AnalyticsRoute from './components/AnalyticsRoute';
import TitledRoute from '@/components/TitledRoute';
import ParticleBackground from '@/components/ux/ParticleBackground';
import AdminLayout from './components/AdminLayout';
import { NavigationProvider } from '@/lib/platform';


function AdminApp() {
  return (
    <NavigationProvider basePath={'/admin'}>
      <AdminAppContent />
    </NavigationProvider>
  );
}

function AdminAppContent() {
  const { isLoaded } = useAuth();
  const [location] = useLocation();
  
  // Scroll to top when route changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location]);

  if (!isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  return (
    <Router base="/admin">
      <div className={"relative"}>
        <ParticleBackground className="absolute inset-0" />
        
        <div className="flex-1">
          <AdminLayout>
            <AnimatePresence mode="wait">
              <Switch>
                {/* Admin routes - base is /admin, so these are relative */}
                <TitledRoute path="/" title="Admin Dashboard - Outclose">
                  <AdminRoute>
                    <AdminDashboard />
                  </AdminRoute>
                </TitledRoute>
                
                <TitledRoute path="/events" title="Admin Events - Outclose">
                  <AdminRoute>
                    <AdminEvents />
                  </AdminRoute>
                </TitledRoute>
                
                <TitledRoute path="/users" title="Admin Users - Outclose">
                  <AdminRoute>
                    <AdminUsers />
                  </AdminRoute>
                </TitledRoute>
                
                <TitledRoute path="/payments" title="Admin Payments - Outclose">
                  <AdminRoute>
                    <AdminPayments />
                  </AdminRoute>
                </TitledRoute>
                
                <TitledRoute path="/feedback" title="Admin Feedback - Outclose">
                  <AdminRoute>
                    <AdminFeedback />
                  </AdminRoute>
                </TitledRoute>
                
                <TitledRoute path="/neighborhoods" title="Admin Neighborhoods - Outclose">
                  <AdminRoute>
                    <AdminNeighborhoods />
                  </AdminRoute>
                </TitledRoute>
                
                <TitledRoute path="/matching/:eventId" title="Admin Matching - Outclose">
                  <AdminRoute>
                    <AdminMatching />
                  </AdminRoute>
                </TitledRoute>

                <TitledRoute path="/ops" title="Event Ops - Outclose">
                  <AdminRoute>
                    <AdminOps />
                  </AdminRoute>
                </TitledRoute>

                <TitledRoute path="/ops/decisions" title="Decision Inbox - Outclose">
                  <AdminRoute>
                    <AdminOpsDecisions />
                  </AdminRoute>
                </TitledRoute>

                <TitledRoute path="/ops/conductor" title="Conductor - Outclose">
                  <AdminRoute>
                    <AdminOpsConductor />
                  </AdminRoute>
                </TitledRoute>

                <TitledRoute path="/ops/activity" title="Automation Activity - Outclose">
                  <AdminRoute>
                    <AdminOpsActivity />
                  </AdminRoute>
                </TitledRoute>

                <TitledRoute path="/ops/no-shows" title="No-show Fees - Outclose">
                  <AdminRoute>
                    <AdminOpsNoShows />
                  </AdminRoute>
                </TitledRoute>

                <TitledRoute path="/triage" title="Feedback Triage - Outclose">
                  <AdminRoute>
                    <AdminTriage />
                  </AdminRoute>
                </TitledRoute>

                <TitledRoute path="/triage/playbook" title="Triage Playbook - Outclose">
                  <AdminRoute>
                    <AdminTriagePlaybook />
                  </AdminRoute>
                </TitledRoute>

                <TitledRoute path="/triage/:ticketId" title="Feedback Triage - Outclose">
                  <AdminRoute>
                    <AdminTriage />
                  </AdminRoute>
                </TitledRoute>

                <TitledRoute path="/ops/:eventId/:neighborhoodId" title="Event Ops Detail - Outclose">
                  <AdminRoute>
                    <AdminOpsEventDetail />
                  </AdminRoute>
                </TitledRoute>
                
                <TitledRoute path="/chats" title="Admin Chats - Outclose">
                  <AdminRoute>
                    <AdminChats />
                  </AdminRoute>
                </TitledRoute>
                
                <TitledRoute path="/polls" title="Admin Polls - Outclose">
                  <AdminRoute>
                    <AdminPolls />
                  </AdminRoute>
                </TitledRoute>
                
                <TitledRoute path="/polls/:pollId" title="Poll Detail - Outclose">
                  <AdminRoute>
                    <AdminPollDetail />
                  </AdminRoute>
                </TitledRoute>
                
                <TitledRoute path="/clubs" title="Admin Clubs - Outclose">
                  <AdminRoute>
                    <AdminClubs />
                  </AdminRoute>
                </TitledRoute>
                
                <TitledRoute path="/clubs/:clubId" title="Club Detail - Outclose">
                  <AdminRoute>
                    <AdminClubDetail />
                  </AdminRoute>
                </TitledRoute>
                
                <TitledRoute path="/connections" title="Admin Connections - Outclose">
                  <AdminRoute>
                    <AdminConnections />
                  </AdminRoute>
                </TitledRoute>
                
                <TitledRoute path="/analytics" title="Analytics - Outclose">
                  <AnalyticsRoute>
                    <AdminAnalytics />
                  </AnalyticsRoute>
                </TitledRoute>
                
                <TitledRoute path="/bookings" title="Bookings - Outclose">
                  <AnalyticsRoute>
                    <AdminBookings />
                  </AnalyticsRoute>
                </TitledRoute>
                
                <TitledRoute path="/demographics" title="Demographics - Outclose">
                  <AnalyticsRoute>
                    <AdminDemographics />
                  </AnalyticsRoute>
                </TitledRoute>

                <TitledRoute path="/landing-variants" title="Landing Variants - Outclose">
                  <AdminRoute>
                    <AdminLandingVariants />
                  </AdminRoute>
                </TitledRoute>

                <TitledRoute path="/landing-variants/:id" title="Edit Landing Variant - Outclose">
                  <AdminRoute>
                    <AdminLandingVariantEditor />
                  </AdminRoute>
                </TitledRoute>

                <TitledRoute path="/host-events" title="Host Event Review - Outclose">
                  <AdminRoute>
                    <AdminHostEvents />
                  </AdminRoute>
                </TitledRoute>

                <TitledRoute path="/social-crawling" title="Social Crawling - Outclose">
                  <AdminRoute>
                    <AdminSocialCrawling />
                  </AdminRoute>
                </TitledRoute>

                <TitledRoute path="/kiki-review" title="Kiki Review - Outclose">
                  <AdminRoute>
                    <AdminKikiReview />
                  </AdminRoute>
                </TitledRoute>

                {/* 404 */}
                <Route>
                  <div className="h-full flex items-center justify-center">
                    <div className="text-center">
                      <h1 className="text-6xl font-bold text-primary-500 mb-4">404</h1>
                      <p className="text-dark-muted">Page not found</p>
                    </div>
                  </div>
                </Route>
              </Switch>
            </AnimatePresence>
          </AdminLayout>
        </div>
      </div>
    </Router>
  );
}

export default AdminApp;

